
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { ExpenseDialog } from "@/components/expenses/ExpenseDialog";
import { ExpenseList } from "@/components/expenses/ExpenseList";
import { useHouses } from "@/hooks/useHouses";

const Expenses = () => {
  const { toast } = useToast();
  const { houses } = useHouses();

  const { data: expenses = [], isLoading: isLoadingExpenses, refetch: refetchExpenses } = useQuery({
    queryKey: ['expenses', houses],
    queryFn: async () => {
      if (!houses.length) return [];

      // First, fetch expenses with category information
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          expense_categories (
            name
          )
        `)
        .in('entity_id', houses.map(house => house.id))
        .eq('entity_type', 'house')
        .order('expense_date', { ascending: false });

      if (error) {
        console.error('Error fetching expenses:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los gastos",
          variant: "destructive",
        });
        throw error;
      }

      // Prepare the result with properly typed data and houses information
      const result = data.map(expense => {
        // Find the house info from our houses array
        const houseInfo = houses.find(h => h.id === expense.entity_id);
        
        return {
          ...expense,
          entity_type: expense.entity_type as 'house' | 'plot',
          houses: houseInfo ? { name: houseInfo.name } : { name: "Casa desconocida" }
        };
      });

      return result;
    },
    enabled: houses.length > 0,
  });

  if (isLoadingExpenses) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-[1600px] mx-auto h-[calc(100vh-65px)]">
      <div className="space-y-6 h-full">
        <div className="flex md:flex-row flex-col md:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800">Gastos</h1>
            <p className="text-muted-foreground mt-1">Revisa los gastos de las casas</p>
          </div>
          <div className="md:w-auto w-full">
            <ExpenseDialog onExpenseCreated={refetchExpenses} entityType="house" />
          </div>
        </div>
        <ExpenseList expenses={expenses} />
      </div>
    </div>
  );
};

export default Expenses;
